import React, { useEffect, useState } from 'react';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import { useForm } from 'react-hook-form';
import { FormSelect } from 'components/molecules/FormSelect';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { ESearchLicencesFormLabelsTypes } from 'constants/SearchFormLabelsTypes';
import {
  initialLegalEntityLicencesSearchFormValues,
  ISearchLegalEntityLicencesFormValues,
} from 'models/form';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';

interface ILicenceSearchForm {
  onSubmit: (arg: ISearchLegalEntityLicencesFormValues) => void;
  isSearchButtonDisabled: boolean;
}

const LicencesSearchForm = ({
  onSubmit,
  isSearchButtonDisabled = false,
}: ILicenceSearchForm) => {
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ISearchLegalEntityLicencesFormValues>({
    defaultValues: initialLegalEntityLicencesSearchFormValues,
  });
  const [values, setValues] = useState<ISearchLegalEntityLicencesFormValues>(
    initialLegalEntityLicencesSearchFormValues,
  );
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { weaponLicenceStatus, loadDictionaries } = useConfigurationLicence();

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ISearchLegalEntityLicencesFormValues);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialLegalEntityLicencesSearchFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.weaponLicenceStatus]);
    })();
  }, [selectedLanguage]);

  return (
    <SearchWrapper margin="0 0 10px 0">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container columnSpacing={2} sx={{ justifyContent: 'space-between' }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormInput
              charsLimit={120}
              maxLength
              name="weaponLicenceNumber"
              label={t(ESearchLicencesFormLabelsTypes.weaponLicenceNumber)}
              setValue={setValue}
              control={control}
              errors={errors}
              withValidation={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <FormSelect
              options={mapDictionaryToOptionProp(weaponLicenceStatus)}
              name="statusUniqueId"
              label={t(ESearchLicencesFormLabelsTypes.statusUniqueId)}
              canBeEmpty
              setValue={setValue}
              control={control}
              errors={errors}
              withValidation={false}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              variant={EButtonVariants.contained}
              size={EButtonSizes.small}
              label={t('legalEntities.individual.accordion.search.button')}
              disabled={isSearchButtonDisabled}
              type="submit"
              fullWidth
              sx={{ mt: 3.5 }}
            />
          </Grid>
        </Grid>
      </form>
      <SelectedFilters
        name="licences"
        values={values}
        labels={ESearchLicencesFormLabelsTypes}
        handleDelete={handleResetField}
        noPreset
        showSaveFilters={false}
        dictionaries={{
          statusUniqueId: prepareFilters(weaponLicenceStatus, 'uniqueId', 'name'),
        }}
        translationKeys={{
          selectedCriteria: 'searchForLegalEntityAncillary.selectedCriteria.label',
        }}
        saveRouterStateAfterClearAll={false}
      />
    </SearchWrapper>
  );
};

export { LicencesSearchForm };
